// Reading the presence roster from components. Every hook selects what it draws and re-renders only when
// that changes (a cursor moving on the stage does not re-render the tree's avatar stacks).
import {
  type PresenceCursor,
  type PresencePeer,
  type PresenceWhere,
  peersAt,
  uniqueUsers,
  visibleCursors,
} from "@filmsquare/realtime/presence";
import { useEffect, useRef, useState } from "react";
import { type CollabCtx, type RosterState, useCollab } from "./CollabProvider.tsx";

const EMPTY: PresencePeer[] = [];

function useRosterSelect<T>(
  c: CollabCtx | null,
  select: (s: RosterState) => T,
  key: (t: T) => string,
  init: T,
): T {
  const [value, setValue] = useState<T>(() => (c ? select(c.store.get()) : init));
  const keyRef = useRef(key(value));
  const sel = useRef(select);
  sel.current = select;
  const k = useRef(key);
  k.current = key;
  useEffect(() => {
    if (!c) return;
    const read = () => {
      const v = sel.current(c.store.get());
      const kk = k.current(v);
      if (kk !== keyRef.current) {
        keyRef.current = kk;
        setValue(v);
      }
    };
    read();
    return c.store.subscribe(read);
  }, [c]);
  return value;
}

const peopleKey = (ps: PresencePeer[]) =>
  ps
    .map((p) => `${p.id}:${p.name}:${p.presence?.where?.label ?? ""}:${p.presence?.where?.id ?? ""}`)
    .join("|");

/** Status of the presence connection ("unavailable" when the server has none). */
export function usePresenceStatus(): RosterState["status"] {
  const c = useCollab();
  return useRosterSelect(
    c,
    (s) => s.status,
    (s) => s,
    "idle",
  );
}

/** The other people in this project, one entry per person (two tabs are one avatar), not me. */
export function useProjectPeople(): PresencePeer[] {
  const c = useCollab();
  return useRosterSelect(
    c,
    (s) => {
      const me = s.roster.self?.userId;
      return uniqueUsers(Object.values(s.roster.peers)).filter((p) => p.userId !== me);
    },
    peopleKey,
    EMPTY,
  );
}

/**
 * Other people who have `where` open (tree folders, cards). With `itemKey`, also the people who have that
 * card selected in a folder (they are looking at it in their inspector): the inspector's "Viewing: …".
 */
export function usePeopleAt(
  where: Pick<PresenceWhere, "kind" | "id"> | null,
  itemKey?: string,
): PresencePeer[] {
  const c = useCollab();
  const kind = where?.kind;
  const id = where?.id;
  return useRosterSelect(
    c,
    (s) => {
      if (!kind || !id) return EMPTY;
      const me = s.roster.self?.userId;
      const peers = Object.values(s.roster.peers);
      const here = peersAt(peers, { kind, id });
      const selecting = itemKey ? peers.filter((p) => p.presence?.selection?.includes(itemKey)) : [];
      return uniqueUsers([...here, ...selecting]).filter((p) => p.userId !== me);
    },
    peopleKey,
    EMPTY,
  );
}

/** Cursors to draw on a stage: only people viewing the same version (visibleCursors). */
export function useStageCursors(
  assetVersionId: string | null,
): { peer: PresencePeer; cursor: PresenceCursor }[] {
  const c = useCollab();
  return useRosterSelect(
    c,
    (s) => {
      const self = s.roster.self;
      if (!self || !assetVersionId) return [];
      return visibleCursors(Object.values(s.roster.peers), {
        id: self.id,
        userId: self.userId,
        presence: { viewing: { assetVersionId } },
      }).filter((x) => x.cursor.space === "stage");
    },
    (l) => l.map((x) => `${x.peer.id}:${x.cursor.x}:${x.cursor.y}`).join("|"),
    [],
  );
}

/** Tell the others which card I have selected in a folder (their inspector shows "Viewing: me"). */
export function usePresenceSelection(key: string | null): void {
  const setPresence = useCollab()?.setPresence;
  useEffect(() => {
    setPresence?.({ selection: key ? [key] : [] });
  }, [setPresence, key]);
}

/** My own connection id (to tell my peer entry from the others'). */
export function useSelfPeer(): RosterState["roster"]["self"] {
  const c = useCollab();
  return useRosterSelect(
    c,
    (s) => s.roster.self,
    (s) => s?.id ?? "",
    null,
  );
}
